import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Terminal, Cpu, Shield, Database, LayoutTemplate, Briefcase, ChevronRight } from 'lucide-react';

export const EnsinoTecnicoLandingPage: React.FC = () => {
  const [unlockedNodes, setUnlockedNodes] = useState<string[]>(['html']);
  const [selectedNode, setSelectedNode] = useState<string>('html');

  const skillTree = [
    { id: 'html', label: 'Lógica & UI', icon: LayoutTemplate, reqs: [], isBase: true },
    { id: 'react', label: 'Frontend React', icon: Terminal, reqs: ['html'] },
    { id: 'db', label: 'Modelagem SQL', icon: Database, reqs: ['html'] },
    { id: 'iot', label: 'IoT & ESP32', icon: Cpu, reqs: ['react', 'db'] },
    { id: 'sec', label: 'Cibersegurança', icon: Shield, reqs: ['iot'] },
  ];

  const nodeDetails: Record<string, any> = {
    html: { title: 'Fundamentos de UI', desc: 'O primeiro passo. Crie interfaces web estáticas e domine algoritmos.', xp: '+100 XP' },
    react: { title: 'Frontend Engineer', desc: 'Aprenda React, Hooks e roteamento para aplicações web complexas.', xp: '+350 XP', vaga: 'Desenvolvedor Jr. (R$ 3.5k)' },
    db: { title: 'Arquiteto de Dados', desc: 'Construa bancos relacionais e APIs REST.', xp: '+300 XP', vaga: 'Backend Trainee (R$ 3.0k)' },
    iot: { title: 'Especialista Embarcado', desc: 'Programe hardware real e envie telemetria para a nuvem.', xp: '+500 XP', vaga: 'Analista IoT (R$ 5.5k)' },
    sec: { title: 'Auditor Cyber', desc: 'O topo da árvore. Defenda redes industriais contra ataques.', xp: '+1000 XP', vaga: 'Consultor Cyber (R$ 8k+)' },
  };

  const handleNodeClick = (id: string) => {
    setSelectedNode(id);
    const node = skillTree.find(n => n.id === id);
    // Auto-unlock if requirements met (simplified logic)
    const canUnlock = node?.reqs.every(req => unlockedNodes.includes(req));
    if (canUnlock && !unlockedNodes.includes(id)) {
      setUnlockedNodes([...unlockedNodes, id]);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-purple-50 font-mono overflow-hidden relative">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.05)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-0"></div>

      <header className="relative z-10 p-6 flex justify-between items-center border-b border-purple-500/20 bg-black/50 backdrop-blur-md">
        <Link to="/exemplos" className="flex items-center gap-2 text-purple-400 hover:text-purple-300">
          <ArrowLeft size={16} /> VOLTAR
        </Link>
        <div className="text-xs tracking-[0.3em] text-purple-500">LABORATORIO_TECNICO_OS</div>
      </header>

      <div className="relative z-10 flex flex-col lg:flex-row h-[calc(100vh-73px)]">
        
        {/* Left: Skill Tree Graph */}
        <div className="flex-1 p-10 flex flex-col items-center justify-center relative">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-black text-white uppercase tracking-widest mb-4">
              Desbloqueie Sua <br/> <span className="text-purple-500">Carreira</span>
            </h1>
            <p className="text-purple-300/70 text-sm max-w-md mx-auto">
              Ganhe XP completando projetos práticos nos laboratórios federais e chame a atenção de recrutadores corporativos.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 max-w-2xl relative">
            {skillTree.map((node) => {
              const isUnlocked = unlockedNodes.includes(node.id);
              const isSelect = selectedNode === node.id;
              const canUnlock = node.reqs.every(r => unlockedNodes.includes(r));

              return (
                <motion.button
                  key={node.id}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => handleNodeClick(node.id)}
                  className={`
                    w-32 h-32 flex flex-col items-center justify-center gap-3 rounded-2xl border-2 transition-all relative overflow-hidden
                    ${isSelect ? 'ring-4 ring-purple-500/50' : ''}
                    ${isUnlocked ? 'bg-purple-900/40 border-purple-500 text-purple-200' : 
                      canUnlock ? 'bg-slate-900 border-slate-700 text-slate-400 cursor-pointer animate-pulse' : 'bg-slate-950 border-slate-900 text-slate-800 cursor-not-allowed opacity-50'}
                  `}
                >
                  {isUnlocked && <div className="absolute inset-0 bg-purple-500/10 animate-[pulse_2s_infinite]"></div>}
                  <node.icon size={28} className={isUnlocked ? "text-purple-400" : "text-slate-600"} />
                  <span className="text-[10px] font-bold text-center px-2 uppercase tracking-wider leading-tight">{node.label}</span>
                </motion.button>
              )
            })}
          </div>
        </div>

        {/* Right: Decoding Terminal Panel */}
        <div className="w-full lg:w-[400px] border-l border-purple-500/20 bg-black/80 backdrop-blur-xl p-8 flex flex-col">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-3 h-3 bg-purple-500 rounded-sm animate-ping"></div>
            <span className="text-xs text-purple-500 tracking-widest uppercase">Análise de Competência</span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={selectedNode}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex-1"
            >
              <h2 className="text-2xl font-black text-white uppercase tracking-wider mb-2">
                {nodeDetails[selectedNode].title}
              </h2>
              <div className="text-purple-400 text-sm font-bold mb-6 bg-purple-500/10 inline-block px-3 py-1 rounded border border-purple-500/30">
                RECOMPENSA: {nodeDetails[selectedNode].xp}
              </div>

              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                {nodeDetails[selectedNode].desc}
              </p>

              {nodeDetails[selectedNode].vaga ? (
                <div className="bg-emerald-950/30 border border-emerald-500/30 p-4 rounded-xl">
                  <p className="text-[10px] text-emerald-500 uppercase tracking-widest mb-1 flex items-center gap-2">
                    <Briefcase size={12} /> Perspectiva de Mercado
                  </p>
                  <p className="text-emerald-300 font-bold">{nodeDetails[selectedNode].vaga}</p>
                </div>
              ) : (
                <div className="bg-slate-900/50 border border-slate-700 p-4 rounded-xl">
                  <p className="text-slate-500 text-xs italic">Continue evoluindo para desbloquear perfis profissionais.</p>
                </div>
              )}

              {!unlockedNodes.includes(selectedNode) && (
                <button 
                  onClick={() => handleNodeClick(selectedNode)}
                  className="mt-8 w-full py-4 bg-purple-600 hover:bg-purple-500 text-white font-black uppercase tracking-widest text-xs rounded transition-colors flex justify-center items-center gap-2"
                >
                  Iniciar Projeto <ChevronRight size={14} />
                </button>
              )}
              {unlockedNodes.includes(selectedNode) && (
                <div className="mt-8 w-full py-4 border border-purple-500 text-purple-400 font-black uppercase tracking-widest text-xs rounded flex justify-center items-center opacity-50">
                  Competência Adquirida
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          
          <div className="mt-auto pt-8 border-t border-white/5 flex items-center gap-4">
            <div className="w-10 h-10 rounded bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold border border-purple-500/30">
              {unlockedNodes.length}
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest">Badges Desbloqueadas</p>
              <div className="w-full bg-slate-900 h-1 mt-1 rounded overflow-hidden">
                <div className="bg-purple-500 h-full transition-all" style={{ width: `${(unlockedNodes.length / skillTree.length) * 100}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
